import React, { Fragment, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Container,
    Card,
    CardBody,
    CardFooter,
    Form,
    Button,
    Label,
    Input,
    FormGroup,
    Row,
    Col,
} from "reactstrap";
import { ToastContainer, toast } from "react-toastify";
import Breadcrumbs from "../../Components/Common/Breadcrumb";
import AxiosInstance from "../../services/AxiosInstance";
import "react-toastify/dist/ReactToastify.css";

const AddCompany = () => {
    const navigate = useNavigate();
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        company_name: "",
        company_email: "",
        company_phone: "",
        company_website: "",
        company_about: "",
        company_linkedin_url: "",
        company_facebook_url: "",
        company_twitter_url: "",
        no_of_employees: "",
        is_verified: 0,
        company_address: "",
        company_city: "",
        company_state: "",
        company_country: "",
        company_zip_code: "",
    });
    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const validate = () => {
        const err = {};
        if (!formData.company_name?.trim()) err.company_name = "Company name is required";
        setErrors(err);
        return Object.keys(err).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setSaving(true);
        try {
            const res = await AxiosInstance().post("/admin/create_company", {
                company_name: formData.company_name.trim(),
                company_email: formData.company_email.trim(),
                company_phone: formData.company_phone.trim(),
                company_website: formData.company_website.trim(),
                company_about: (formData.company_about || "").trim(),
                company_linkedin_url: (formData.company_linkedin_url || "").trim(),
                company_facebook_url: (formData.company_facebook_url || "").trim(),
                company_twitter_url: (formData.company_twitter_url || "").trim(),
                company_address: formData.company_address.trim(),
                company_city: formData.company_city.trim(),
                company_state: formData.company_state.trim(),
                company_country: formData.company_country.trim(),
                company_zip_code: formData.company_zip_code.trim(),
                no_of_employees: formData.no_of_employees.trim(),
                is_verified: formData.is_verified ? 1 : 0,
            });
            if (res.data.status) {
                toast.success(res.data.message || "Company created successfully");
                navigate("/job/admin/manage-companies");
            } else {
                toast.error(res.data.message || "Failed to create company");
            }
        } catch (err) {
            toast.error(err.response?.data?.message || "Request failed");
        } finally {
            setSaving(false);
        }
    };

    const goBack = () => navigate("/job/admin/manage-companies");

    return (
        <Fragment>
            <Breadcrumbs title="Add Company" mainTitle="Admin" />
            <Container fluid={true}>
                <Form onSubmit={handleSubmit} className="theme-form">
                    <Card>
                        <CardBody>
                            {/* About the Company */}
                            <h5 className="mb-4">About the Company</h5>
                            <Row>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label className="col-form-label m-0 pt-0">
                                            Company Name<span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            className="form-control form-control-sm"
                                            placeholder="Enter company name"
                                            id="company_name"
                                            name="company_name"
                                            value={formData.company_name}
                                            onChange={handleChange}
                                            invalid={!!errors.company_name}
                                        />
                                        {errors.company_name && (
                                            <small className="text-danger">{errors.company_name}</small>
                                        )}
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Company Email</Label>
                                        <Input
                                            className="form-control form-control-sm"
                                            placeholder="Enter company email"
                                            id="company_email"
                                            name="company_email"
                                            type="email"
                                            value={formData.company_email}
                                            onChange={handleChange}
                                        />
                                        {errors.company_email && (
                                            <small className="text-danger">{errors.company_email}</small>
                                        )}
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Company Phone</Label>
                                        <Input
                                            className="form-control form-control-sm"
                                            placeholder="Enter phone number"
                                            id="company_phone"
                                            name="company_phone"
                                            value={formData.company_phone}
                                            onChange={handleChange}
                                        />
                                        {errors.company_phone && (
                                            <small className="text-danger">{errors.company_phone}</small>
                                        )}
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Company Website URL</Label>
                                        <Input
                                            className="form-control form-control-sm"
                                            placeholder="Enter company website URL"
                                            id="company_website"
                                            name="company_website"
                                            type="url"
                                            value={formData.company_website}
                                            onChange={handleChange}
                                        />
                                        {errors.company_website && (
                                            <small className="text-danger">{errors.company_website}</small>
                                        )}
                                    </FormGroup>
                                </Col>
                                <Col md={12}>
                                    <FormGroup>
                                        <Label>About Company</Label>
                                        <Input
                                            type="textarea"
                                            className="form-control"
                                            placeholder="Enter about the company"
                                            id="company_about"
                                            name="company_about"
                                            rows={6}
                                            value={formData.company_about}
                                            onChange={handleChange}
                                            maxLength={250}
                                        />
                                        {errors.company_about && (
                                            <small className="text-danger">{errors.company_about}</small>
                                        )}
                                    </FormGroup>
                                </Col>
                            </Row>

                            {/* Social Media */}
                            <h5 className="mb-4">Social Media</h5>
                            <Row>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>LinkedIn URL</Label>
                                        <Input
                                            className="form-control form-control-sm"
                                            placeholder="Enter LinkedIn URL"
                                            id="company_linkedin_url"
                                            name="company_linkedin_url"
                                            value={formData.company_linkedin_url}
                                            onChange={handleChange}
                                        />
                                        {errors.company_linkedin_url && (
                                            <small className="text-danger">{errors.company_linkedin_url}</small>
                                        )}
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Facebook URL</Label>
                                        <Input
                                            className="form-control form-control-sm"
                                            placeholder="Enter Facebook URL"
                                            id="company_facebook_url"
                                            name="company_facebook_url"
                                            value={formData.company_facebook_url}
                                            onChange={handleChange}
                                        />
                                        {errors.company_facebook_url && (
                                            <small className="text-danger">{errors.company_facebook_url}</small>
                                        )}
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Twitter URL</Label>
                                        <Input
                                            className="form-control form-control-sm"
                                            placeholder="Enter Twitter URL"
                                            id="company_twitter_url"
                                            name="company_twitter_url"
                                            value={formData.company_twitter_url}
                                            onChange={handleChange}
                                        />
                                        {errors.company_twitter_url && (
                                            <small className="text-danger">{errors.company_twitter_url}</small>
                                        )}
                                    </FormGroup>
                                </Col>
                            </Row>

                            {/* Business Information */}
                            <h5 className="mb-4">Business Information</h5>
                            <Row>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Number of Employees</Label>
                                        <Input
                                            className="form-control form-control-sm"
                                            placeholder="Enter number of employees"
                                            id="no_of_employees"
                                            name="no_of_employees"
                                            value={formData.no_of_employees}
                                            onChange={handleChange}
                                        />
                                        {errors.no_of_employees && (
                                            <small className="text-danger">{errors.no_of_employees}</small>
                                        )}
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup className="d-flex align-items-end h-100 pb-2">
                                        <div className="form-check form-switch">
                                            <Input
                                                type="switch"
                                                className="form-check-input"
                                                id="is_verified"
                                                name="is_verified"
                                                checked={!!formData.is_verified}
                                                onChange={(e) =>
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        is_verified: e.target.checked ? 1 : 0,
                                                    }))
                                                }
                                            />
                                            <Label className="form-check-label" htmlFor="is_verified">
                                                Verified
                                            </Label>
                                        </div>
                                    </FormGroup>
                                </Col>
                            </Row>

                            {/* Address */}
                            <h5 className="mb-4">Address</h5>
                            <Row>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Company Address</Label>
                                        <Input
                                            className="form-control form-control-sm"
                                            placeholder="Enter company address"
                                            id="company_address"
                                            name="company_address"
                                            value={formData.company_address}
                                            onChange={handleChange}
                                            maxLength={100}
                                        />
                                        {errors.company_address && (
                                            <small className="text-danger">{errors.company_address}</small>
                                        )}
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Company Country</Label>
                                        <Input
                                            className="form-control form-control-sm"
                                            placeholder="Enter company country"
                                            id="company_country"
                                            name="company_country"
                                            value={formData.company_country}
                                            onChange={handleChange}
                                        />
                                        {errors.company_country && (
                                            <small className="text-danger">{errors.company_country}</small>
                                        )}
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Company State</Label>
                                        <Input
                                            className="form-control form-control-sm"
                                            placeholder="Enter company state"
                                            id="company_state"
                                            name="company_state"
                                            value={formData.company_state}
                                            onChange={handleChange}
                                        />
                                        {errors.company_state && (
                                            <small className="text-danger">{errors.company_state}</small>
                                        )}
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Company City</Label>
                                        <Input
                                            className="form-control form-control-sm"
                                            placeholder="Enter company city"
                                            id="company_city"
                                            name="company_city"
                                            value={formData.company_city}
                                            onChange={handleChange}
                                        />
                                        {errors.company_city && (
                                            <small className="text-danger">{errors.company_city}</small>
                                        )}
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Company Zip Code</Label>
                                        <Input
                                            className="form-control form-control-sm"
                                            placeholder="Enter company zip code"
                                            id="company_zip_code"
                                            name="company_zip_code"
                                            value={formData.company_zip_code}
                                            onChange={handleChange}
                                            maxLength={10}
                                        />
                                        {errors.company_zip_code && (
                                            <small className="text-danger">{errors.company_zip_code}</small>
                                        )}
                                    </FormGroup>
                                </Col>
                            </Row>
                        </CardBody>
                        <CardFooter className="text-end">
                            <Button type="button" color="secondary" className="me-2" onClick={goBack}>
                                Cancel
                            </Button>
                            <Button color="primary" type="submit" disabled={saving}>
                                {saving ? "Creating..." : "Create Company"}
                            </Button>
                        </CardFooter>
                    </Card>
                </Form>
            </Container>
            <ToastContainer />
        </Fragment>
    );
};

export default AddCompany;
